import React, { useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import '../styles/editprofile.css';
import ProfilePicture from './ProfilePicture';

const REGIONS = [
    { slug: 'centurion',      label: 'Centurion' },
    { slug: 'eastern_cape',   label: 'Eastern Cape' },
    { slug: 'freestate',      label: 'Free State' },
    { slug: 'johannesburg',   label: 'Johannesburg' },
    { slug: 'kwa_zulu_natal', label: 'KwaZulu-Natal' },
    { slug: 'limpopo',        label: 'Limpopo' },
    { slug: 'mpumalanga',     label: 'Mpumalanga' },
    { slug: 'north_cape',     label: 'Northern Cape' },
    { slug: 'north_west',     label: 'North West' },
    { slug: 'pretoria',       label: 'Pretoria' },
    { slug: 'southern_cape',  label: 'Southern Cape' },
    { slug: 'western_cape',   label: 'Western Cape' },
];

const EditProfileModal = ({ user, onClose, onUpdate, token }) => {
    const [formData, setFormData] = useState({
        username: user.username || '',
        email: user.email || '',
        bio: user.bio || '',
        address: {
            street: user.address?.street || '',
            city: user.address?.city || '',
            postalCode: user.address?.postalCode || '',
            province: user.address?.province || ''
        }
    });
    const [preferredRegion, setPreferredRegion] = useState(
        user.preferredRegion || localStorage.getItem('preferredRegion') || ''
    );
    const [vitalityFirst, setVitalityFirst] = useState(
        user.vitalityFirst ?? (localStorage.getItem('vitalityFirst') === 'true')
    );
    const [profilePicture, setProfilePicture] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(user.profilePicture || '');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            setFormData(prev => ({
                ...prev,
                [parent]: {
                    ...prev[parent],
                    [child]: value
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setProfilePicture(file);
            setPreviewUrl(URL.createObjectURL(file));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const formDataToSend = new FormData();
            formDataToSend.append('userId', user._id);
            formDataToSend.append('username', formData.username);
            formDataToSend.append('email', formData.email);
            formDataToSend.append('bio', formData.bio);
            formDataToSend.append('address', JSON.stringify(formData.address));
            
            if (profilePicture) {
                formDataToSend.append('profilePicture', profilePicture);
            }

            // Run profile update and preferences update in parallel
            const [profileResponse] = await Promise.all([
                axios.put(
                    `${API_BASE_URL}/users/profile/update`,
                    formDataToSend,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'multipart/form-data'
                        }
                    }
                ),
                axios.patch(
                    `${API_BASE_URL}/users/preferences`,
                    { preferredRegion: preferredRegion || null, vitalityFirst },
                    { headers: { Authorization: `Bearer ${token}` } }
                )
            ]);

            // Persist preferences in localStorage so other components pick them up immediately
            localStorage.setItem('preferredRegion', preferredRegion || '');
            localStorage.setItem('vitalityFirst', vitalityFirst ? 'true' : 'false');
            window.dispatchEvent(new CustomEvent('region-updated', {
                detail: { preferredRegion, vitalityFirst }
            }));

            if (profileResponse.data?.user) {
                onUpdate({
                    ...profileResponse.data.user,
                    preferredRegion: preferredRegion || null,
                    vitalityFirst
                });
                onClose();
            } else {
                setError('Profile update failed - please try again');
            }
        } catch (error) {
            console.error('Profile update error:', error);
            setError(error.response?.data?.message || 'Error updating profile');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="edit-profile-modal">
            <div className="modal-content">
                <h2>Edit Profile</h2>
                {error && <div className="error-message">{error}</div>}
                <form onSubmit={handleSubmit}>
                    <div className="profile-picture-section">
                        <ProfilePicture 
                            src={previewUrl}
                            alt="Profile Preview"
                            className="profile-preview"
                        />
                        <input
                            type="file"
                            accept="image/*"
                            onChange={handleImageChange}
                            id="profile-picture"
                        />
                        <label htmlFor="profile-picture" className="upload-button">
                            Change Profile Picture
                        </label>
                    </div>

                    <div className="form-group">
                        <label>Username</label>
                        <input
                            type="text"
                            name="username"
                            value={formData.username}
                            onChange={handleInputChange}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>Email</label>
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>Bio</label>
                        <textarea
                            name="bio"
                            value={formData.bio}
                            onChange={handleInputChange}
                            rows="3"
                        />
                    </div>

                    <div className="address-section">
                        <h3>Address</h3>
                        <div className="form-group">
                            <label>Street</label>
                            <input
                                type="text"
                                name="address.street"
                                value={formData.address.street}
                                onChange={handleInputChange}
                            />
                        </div>
                        <div className="form-group">
                            <label>City</label>
                            <input
                                type="text"
                                name="address.city"
                                value={formData.address.city}
                                onChange={handleInputChange}
                            />
                        </div>
                        <div className="form-group">
                            <label>Postal Code</label>
                            <input
                                type="text"
                                name="address.postalCode"
                                value={formData.address.postalCode}
                                onChange={handleInputChange}
                            />
                        </div>
                        <div className="form-group">
                            <label>Province</label>
                            <input
                                type="text"
                                name="address.province"
                                value={formData.address.province}
                                onChange={handleInputChange}
                            />
                        </div>
                    </div>

                    <div className="shopping-prefs-section">
                        <h3>🛒 Shopping Preferences</h3>
                        <div className="form-group">
                            <label>My Region</label>
                            <p className="prefs-hint">
                                We use this to show you ingredient prices from stores near you.
                            </p>
                            <div className="region-grid">
                                {REGIONS.map(r => (
                                    <button
                                        key={r.slug}
                                        type="button"
                                        className={`region-btn${preferredRegion === r.slug ? ' region-btn--active' : ''}`}
                                        onClick={() => setPreferredRegion(r.slug)}
                                    >
                                        {preferredRegion === r.slug && '✓ '}
                                        {r.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="vitality-row">
                            <input
                                type="checkbox"
                                id="vitality-first-toggle"
                                checked={vitalityFirst}
                                onChange={e => setVitalityFirst(e.target.checked)}
                                className="vitality-checkbox"
                            />
                            <label htmlFor="vitality-first-toggle" className="vitality-label">
                                <strong>Vitality First</strong>
                                <span className="prefs-hint"> — show Discovery Vitality products at the top of ingredient lists</span>
                            </label>
                        </div>
                    </div>

                    <div className="button-group">
                        <button type="submit" disabled={loading}>
                            {loading ? 'Saving...' : 'Save Changes'}
                        </button>
                        <button type="button" onClick={onClose}>
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditProfileModal; 